import { FC, useState } from 'react'
import Swal from 'sweetalert2'
import { KTIcon } from '../../../../../_metronic/helpers'
import { setMerchantMatchingAutoHaltClients } from './api'
import {
  MATCHING_AUTO_HALT_DEFAULT_CLIENTS,
  MATCHING_AUTO_HALT_MAX_CLIENTS,
  MATCHING_AUTO_HALT_MIN_CLIENTS,
} from './types'

interface Props {
  merchantId: number
  clients: number | null
}

// Only a positive integer without leading zeros, or an empty field. The gate refuses the keystroke
// instead of cleaning it up afterwards, so the caret does not jump.
const CLIENTS_INPUT_RE = /^([1-9]\d*)?$/

const toDraft = (clients: number | null) => (clients === null ? '' : String(clients))

const MerchantMatchingAutoHalt: FC<Props> = ({ merchantId, clients }) => {
  const [saved, setSaved] = useState<number | null>(clients)
  const [draft, setDraft] = useState(toDraft(clients))
  const [saving, setSaving] = useState(false)

  // Below the floor the value is not offered for saving: one client is not a series.
  const valid = draft === '' || Number(draft) >= MATCHING_AUTO_HALT_MIN_CLIENTS
  const dirty = draft !== toDraft(saved) && valid

  const onChange = (value: string) => {
    if (!CLIENTS_INPUT_RE.test(value)) return
    if (value !== '' && Number(value) > MATCHING_AUTO_HALT_MAX_CLIENTS) return
    setDraft(value)
  }

  const apply = async () => {
    const next = draft === '' ? null : Number(draft)
    setSaving(true)
    try {
      const res = await setMerchantMatchingAutoHaltClients(merchantId, next)
      if (!res?.data?.success) {
        throw new Error('save_failed')
      }
      setSaved(next)
      Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Порог автоостановки обновлён', showConfirmButton: false, timer: 2500 })
    } catch {
      Swal.fire({ toast: true, position: 'top', icon: 'error', title: 'Не удалось сохранить порог автоостановки', showConfirmButton: false, timer: 4000 })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='d-flex flex-column gap-1' style={{ maxWidth: 320 }}>
      <div className='d-flex align-items-center gap-2'>
        <input
          type='text'
          inputMode='numeric'
          aria-label='Мэтчинг: автоостановка выплаты (клиентов)'
          className='form-control form-control-sm'
          style={{ maxWidth: 100 }}
          placeholder={String(MATCHING_AUTO_HALT_DEFAULT_CLIENTS)}
          value={draft}
          disabled={saving}
          onChange={(e) => onChange(e.target.value)}
        />
        {dirty && (
          <>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-success'
              title='Применить'
              disabled={saving}
              onClick={apply}
            >
              {saving ? (
                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
              ) : (
                <KTIcon iconName='check' className='fs-2' />
              )}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-danger'
              title='Сбросить'
              disabled={saving}
              onClick={() => setDraft(toDraft(saved))}
            >
              <KTIcon iconName='cross' className='fs-2' />
            </button>
          </>
        )}
      </div>
      <div className='text-muted fs-8'>
        {`${MATCHING_AUTO_HALT_MIN_CLIENTS}–${MATCHING_AUTO_HALT_MAX_CLIENTS} клиентов подряд; пусто — ${MATCHING_AUTO_HALT_DEFAULT_CLIENTS}`}
      </div>
    </div>
  )
}

export default MerchantMatchingAutoHalt
